import { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';
import logo from '../assets/images/BESTMARK DESIGNERS 13.png';

const NAV_LINKS = [
  { label: 'Home',       path: '/' },
  { label: 'About Us',   path: '/about' },
  { label: 'Services',   path: '/services' },
  { label: 'Portfolio',  path: '/portfolio' },
  { label: 'Contact Us', path: '/contact' },
];

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  // Scroll detection
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // Navigate, close mobile menu, scroll to top
  const handleNav = (path) => {
    navigate(path);
    setMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        .bm-nav *, .bm-nav *::before, .bm-nav *::after,
        .bm-mobile-menu *, .bm-mobile-menu *::before, .bm-mobile-menu *::after {
          box-sizing: border-box; margin: 0; padding: 0;
        }

        /* ── Desktop Nav ─────────────────────────── */
        .bm-nav {
          position: fixed; top: 0; left: 0; right: 0; z-index: 300;
          padding: 0 clamp(16px, 5vw, 80px);
          background: transparent;
          transition: background 0.35s ease, box-shadow 0.35s ease, backdrop-filter 0.35s ease;
        }
        .bm-nav.scrolled {
          background: rgba(10, 10, 10, 0.88);
          backdrop-filter: blur(22px);
          -webkit-backdrop-filter: blur(22px);
          box-shadow: 0 1px 0 rgba(227,30,36,0.2), 0 4px 32px rgba(0,0,0,0.45);
        }
        .bm-nav-inner {
          max-width: 1280px; margin: 0 auto;
          display: flex; align-items: center; justify-content: space-between;
          height: 72px; gap: 24px;
        }

        /* Logo */
        .bm-nav-logo {
          flex-shrink: 0; display: flex; align-items: center; cursor: pointer;
        }
        .bm-nav-logo img {
          height: 220px; width: auto; object-fit: contain; display: block;
        }

        /* Desktop links — NavLink provides .active class automatically */
        .bm-nav-links {
          display: flex; align-items: center; gap: 36px;
          list-style: none; flex: 1; justify-content: center;
        }
        .bm-nav-links a {
          font-family: 'Inter', sans-serif;
          color: rgba(255,255,255,0.7); text-decoration: none;
          font-size: 14px; font-weight: 500; letter-spacing: 0.25px;
          position: relative; padding-bottom: 4px;
          transition: color 0.2s; white-space: nowrap;
        }
        .bm-nav-links a::after {
          content: ''; position: absolute; bottom: 0; left: 0;
          width: 0; height: 2px; background: #E31E24; border-radius: 2px;
          transition: width 0.28s cubic-bezier(0.22,1,0.36,1);
        }
        .bm-nav-links a:hover { color: #fff; }
        .bm-nav-links a:hover::after { width: 100%; }

        /* NavLink adds this class when the route matches */
        .bm-nav-links a.active {
          color: #fff; font-weight: 600;
        }
        .bm-nav-links a.active::after { width: 100%; }

        /* CTA button */
        .bm-nav-cta {
          flex-shrink: 0; display: inline-flex; align-items: center; gap: 8px;
          font-family: 'Inter', sans-serif; font-size: 13.5px; font-weight: 700;
          letter-spacing: 0.35px; color: #fff; background: #E31E24;
          padding: 10px 22px; border-radius: 7px; border: none;
          cursor: pointer; white-space: nowrap;
          transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
        }
        .bm-nav-cta:hover {
          background: #c81920; transform: translateY(-1px);
          box-shadow: 0 8px 24px rgba(227,30,36,0.38);
        }
        .bm-nav-cta:active { transform: translateY(0); }

        /* Hamburger — hidden on desktop, shown on mobile */
        .bm-hamburger {
          display: none; align-items: center; justify-content: center;
          background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12);
          border-radius: 8px; padding: 8px; color: #fff;
          cursor: pointer; flex-shrink: 0;
          transition: background 0.2s, border-color 0.2s;
        }
        .bm-hamburger:hover {
          background: rgba(227,30,36,0.12); border-color: rgba(227,30,36,0.3);
        }

        /* ── Mobile Menu ──────────────────────────── */
        .bm-mobile-menu {
          position: fixed; inset: 0; z-index: 400;
          background: rgba(10,10,10,0.97);
          backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px);
          display: flex; flex-direction: column;
          align-items: center; justify-content: center; gap: 8px;
          transform: translateX(100%);
          transition: transform 0.38s cubic-bezier(0.77,0,0.175,1);
        }
        .bm-mobile-menu.open { transform: translateX(0); }
        .bm-mobile-menu::after {
          content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 3px;
          background: linear-gradient(90deg, transparent, #E31E24, transparent);
        }

        .bm-mobile-top {
          position: absolute; top: 0; left: 0; right: 0;
          display: flex; align-items: center; justify-content: space-between;
          padding: 20px clamp(16px, 5vw, 32px);
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }
        .bm-mobile-top img {
          height: 44px; width: auto; object-fit: contain; cursor: pointer;
        }

        .bm-close {
          display: flex; align-items: center; justify-content: center;
          background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12);
          border-radius: 8px; padding: 9px; color: #fff; cursor: pointer;
          transition: background 0.2s, border-color 0.2s;
        }
        .bm-close:hover {
          background: rgba(227,30,36,0.14); border-color: rgba(227,30,36,0.32);
        }

        /* Mobile nav links — NavLink also applies .active here */
        .bm-mobile-link {
          font-family: 'Inter', sans-serif;
          color: rgba(255,255,255,0.82); text-decoration: none;
          font-size: clamp(26px, 5vw, 38px); font-weight: 800;
          letter-spacing: -0.5px; padding: 10px 24px; border-radius: 10px;
          transition: color 0.2s, background 0.2s;
          width: 100%; max-width: 320px; text-align: center;
        }
        .bm-mobile-link:hover { color: #E31E24; background: rgba(227,30,36,0.06); }
        .bm-mobile-link.active { color: #E31E24; background: rgba(227,30,36,0.08); }

        .bm-mobile-cta {
          display: inline-flex; align-items: center; gap: 8px;
          font-family: 'Inter', sans-serif; font-size: 15px; font-weight: 700;
          color: #fff; background: #E31E24; border: none; cursor: pointer;
          padding: 14px 32px; border-radius: 8px; margin-top: 16px;
          transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
        }
        .bm-mobile-cta:hover {
          background: #c81920; transform: translateY(-1px);
          box-shadow: 0 10px 28px rgba(227,30,36,0.38);
        }

        /* ── Responsive ───────────────────────────── */
        @media (max-width: 1024px) {
          .bm-nav-links, .bm-nav-cta { display: none; }
          .bm-hamburger { display: flex; }
        }

        /* Focus rings for keyboard navigation */
        .bm-nav-links a:focus-visible,
        .bm-nav-cta:focus-visible,
        .bm-hamburger:focus-visible,
        .bm-close:focus-visible,
        .bm-mobile-link:focus-visible,
        .bm-mobile-cta:focus-visible {
          outline: 2px solid #E31E24; outline-offset: 3px; border-radius: 4px;
        }

        @media (prefers-reduced-motion: reduce) {
          .bm-nav, .bm-mobile-menu,
          .bm-nav-links a::after,
          .bm-nav-cta, .bm-mobile-link, .bm-mobile-cta {
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>

      {/* ── Desktop Nav ── */}
      <nav
        className={`bm-nav${scrolled ? ' scrolled' : ''}`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="bm-nav-inner">

          {/* Logo — clicking goes home */}
          <div className="bm-nav-logo" onClick={() => handleNav('/')}>
            <img src={logo} alt="Bestmark Designers" />
          </div>

          {/* Desktop links */}
          <ul className="bm-nav-links">
            {NAV_LINKS.map(({ label, path }) => (
              <li key={label}>
                <NavLink
                  to={path}
                  end={path === '/'}          /* "end" = exact match for home only */
                  aria-current={({ isActive }) => isActive ? 'page' : undefined}
                >
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>

          {/* Desktop CTA */}
          <button
            className="bm-nav-cta"
            onClick={() => handleNav('/contact')}
            aria-label="Get a free quote"
          >
            Get a Free Quote <ArrowRight size={14} />
          </button>

          {/* Hamburger */}
          <button
            className="bm-hamburger"
            onClick={() => setMenuOpen(true)}
            aria-label="Open navigation menu"
            aria-expanded={menuOpen}
            aria-controls="bm-mobile-menu"
          >
            <Menu size={24} />
          </button>
        </div>
      </nav>

      {/* ── Mobile Menu ── */}
      <div
        id="bm-mobile-menu"
        className={`bm-mobile-menu${menuOpen ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        {/* Top bar: logo + close */}
        <div className="bm-mobile-top">
          <img
            src={logo}
            alt="Bestmark Designers"
            onClick={() => handleNav('/')}
          />
          <button
            className="bm-close"
            onClick={() => setMenuOpen(false)}
            aria-label="Close navigation menu"
          >
            <X size={22} />
          </button>
        </div>

        {/* Mobile nav links */}
        {NAV_LINKS.map(({ label, path }) => (
          <NavLink
            key={label}
            to={path}
            end={path === '/'}
            className={({ isActive }) =>
              `bm-mobile-link${isActive ? ' active' : ''}`
            }
            onClick={() => handleNav(path)}
          >
            {label}
          </NavLink>
        ))}

        {/* Mobile CTA */}
        <button
          className="bm-mobile-cta"
          onClick={() => handleNav('/contact')}
        >
          Get a Free Quote <ArrowRight size={15} />
        </button>
      </div>
    </>
  );
}